import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DebugRequestSectionProps } from './types';
import CopyButton from './CopyButton';
import DebugStatusChip from './DebugStatusChip';
import JsonViewer from './JsonViewer';

/**
 * Collapsible section for a single request/response pair
 */
const DebugRequestSection: React.FC<DebugRequestSectionProps> = ({
  request,
  defaultExpanded = false,
}) => {
  const {
    method,
    url,
    path,
    requestHeaders,
    requestBody,
    responseStatus,
    responseBody,
    durationMs,
    error,
    system,
    category,
    timestamp,
  } = request;

  // Format duration
  const durationLabel = durationMs !== undefined ? `${durationMs}ms` : '';

  // Format time
  const timeLabel = new Date(timestamp).toLocaleTimeString();

  // Build complete request object for copying
  const fullRequestData = {
    timestamp,
    system,
    method,
    url,
    path,
    requestHeaders,
    requestBody,
    responseStatus,
    responseBody,
    durationMs,
    error,
  };

  // Method color
  const getMethodColor = (m: string) => {
    switch (m.toUpperCase()) {
      case 'GET':
        return 'info.main';
      case 'POST':
        return 'success.main';
      case 'PUT':
      case 'PATCH':
        return 'warning.main';
      case 'DELETE':
        return 'error.main';
      default:
        return 'grey.600';
    }
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 1,
        mb: 1,
        borderRadius: 1,
        overflow: 'hidden',
        ...(error && {
          borderLeft: '4px solid',
          borderLeftColor: 'error.main',
        }),
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: error ? 'error.50' : 'grey.50',
          '&:hover': {
            backgroundColor: error ? 'error.100' : 'grey.100',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {/* Method badge */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold',
              color: getMethodColor(method),
              fontFamily: 'monospace',
              minWidth: 50,
            }}
          >
            {method}
          </Typography>

          {/* Status chip */}
          <DebugStatusChip status={responseStatus} error={error} />

          {/* Path */}
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              color: 'grey.700',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {path}
          </Typography>

          {/* System & category chips */}
          <Chip label={system} size="small" variant="outlined" />
          {category && (
            <Chip
              label={category}
              size="small"
              color={
                category === 'publish' || category === 'poll_result'
                  ? 'primary'
                  : 'default'
              }
              variant="outlined"
            />
          )}

          {/* Duration */}
          {durationLabel && (
            <Typography variant="caption" color="text.secondary">
              {durationLabel}
            </Typography>
          )}

          {/* Time */}
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>

          {/* Copy button */}
          <CopyButton
            content={fullRequestData}
            tooltip="Copy request/response"
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {/* Error message if present */}
        {error && (
          <Box sx={{ p: 2, backgroundColor: 'error.50' }}>
            <Typography variant="subtitle2" color="error.main" gutterBottom>
              Error
            </Typography>
            <Typography variant="body2" color="error.dark">
              {error}
            </Typography>
          </Box>
        )}

        {/* URL */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            URL
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </Typography>
            <CopyButton content={url} tooltip="Copy URL" />
          </Box>
        </Box>

        <Divider />

        {/* Request Headers - collapsed by default */}
        {requestHeaders && Object.keys(requestHeaders).length > 0 && (
          <>
            <Accordion disableGutters sx={{ boxShadow: 'none' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">Request Headers</Typography>
                  <CopyButton content={requestHeaders} tooltip="Copy headers" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <JsonViewer data={requestHeaders} maxHeight={200} />
              </AccordionDetails>
            </Accordion>
            <Divider />
          </>
        )}

        {/* Request Body */}
        {requestBody !== undefined && (
          <>
            <Accordion disableGutters sx={{ boxShadow: 'none' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">Request Body</Typography>
                  <CopyButton
                    content={requestBody}
                    tooltip="Copy request body"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <JsonViewer data={requestBody} />
              </AccordionDetails>
            </Accordion>
            <Divider />
          </>
        )}

        {/* Response Body */}
        {responseBody !== undefined && (
          <Accordion disableGutters sx={{ boxShadow: 'none' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ backgroundColor: 'grey.50' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Response Body</Typography>
                <CopyButton
                  content={responseBody}
                  tooltip="Copy response body"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <JsonViewer data={responseBody} />
            </AccordionDetails>
          </Accordion>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(DebugRequestSection);
